import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, withLatestFrom, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TraderTrialInfoAction } from './trader-trial.action';
import { HttpHeaders } from '@angular/common/http';
import { HttpService } from '@toro/common/shared/services/http/http.service';
import { SettingsService } from '@toro/common/shared/services/settings/settings.service';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { UsersState } from '@toro/common/shared/state-management/users/users.reducer';

@Injectable()
export class TraderTrialInfoEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private httpService: HttpService,
    private settings: SettingsService
  ) {}

  traderTrialInfo = createEffect(() =>
    this.actions$.pipe(
      ofType(TraderTrialInfoAction.traderTrialInfo),
      withLatestFrom(this.store.select((authStore) => authStore.authentication)),
      switchMap(([, auth]) => {
      const apiUrl = this.settings.getConfigurationKeyValue('backend');
      const isLogged = !!(auth.currentUser && auth.currentUser.token);
      const headers = this._createHeaders(auth, isLogged);
      return this.httpService
        .get<any>(
          `${apiUrl.simApi}trader-trial/${auth.currentUser.userId}`,
          headers
        )
        .pipe(
          map((traderTrialResponse: any) => {
            if (traderTrialResponse && traderTrialResponse.length > 0) {
              const convertedPayload = traderTrialResponse.map((trial) => ({
                ...trial,
                purchasedAt: new Date(trial.purchasedAt),
                expiresAt: new Date(trial.expiresAt),
                updatedAt: new Date(trial.updatedAt),
                createdAt: new Date(trial.createdAt),
              }));
              return {
                type: TraderTrialInfoAction.traderTrialInfoSuccess,
                payload: convertedPayload,
              };
            } else {
              return {
                type: TraderTrialInfoAction.traderTrialInfoSuccess,
                payload: [],
              };
            }
          })
        );
    }),
    catchError((error) =>
      of({ type: TraderTrialInfoAction.traderTrialInfoError, payload: error })
    )
  ));

  activateTrialTrader$ = createEffect(() => this.actions$.pipe(
    ofType(TraderTrialInfoAction.activateTrialTrader),
    withLatestFrom(this.store.select((authStore) => authStore.authentication)),
    switchMap(([, auth]) => {
      const apiUrl = this.settings.getConfigurationKeyValue('backend');
      const isLogged = !!(auth.currentUser && auth.currentUser.token);
      const userId = auth.currentUser ? auth.currentUser.userId : null;
      const body = { userId };
      const headers = this._createHeaders(auth, isLogged);
      return this.httpService
        .post<any>(`${apiUrl.simApi}trader-trial/`, body, headers)
        .pipe(
          map((traderResponse: any) => {
            if (traderResponse) {
              return {
                type: TraderTrialInfoAction.activateTrialTraderSuccess,
                payload: traderResponse,
              };
            } else {
              return {
                type: TraderTrialInfoAction.activateTrialTraderError,
                payload: {},
              };
            }
          })
        );
    }),
    catchError((error) =>
      of({
        type: TraderTrialInfoAction.activateTrialTraderError,
        payload: error,
      })
    )
  ));

  private _createHeaders(userData: UsersState, logged: boolean = false) {
    if (!logged) {
      return new HttpHeaders({
        'Content-Type': 'application/json; charset=UTF-8',
        'X-SessionID': `${userData.sessionID}`,
      });
    } else {
      return new HttpHeaders({
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${userData.currentUser.token}`,
      });
    }
  }
}
